import { type FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { ChallengeRequestScreenState } from '@shinetools/sca-operations';
import {
  Button,
  Typography,
  View,
  YStack,
} from '@shinetools/sunshine-universal';

import { GenerateShortLivedTokenDocument } from 'common/hooks/useAuthenticatedAction/generateShortLivedToken.gql';
import SunshineModal from 'components/_core/SunshineModal';
import PasscodeInput from 'components/PasscodeInput';
import { type ClearAllDigitsFn } from 'components/PasscodeInput/types';
import { useDevice } from 'helpers/device';

import { modalMinHeight } from '../utils/constant';

export interface ChallengeRequestPasscodeProps {
  challengeId: string;
  operation: string;
  userId: string;
  originDeviceId: string;
  payload: Record<string, unknown>;
  moveState: (state: ChallengeRequestScreenState) => void;
}

const ChallengeRequestPasscode: FC<ChallengeRequestPasscodeProps> = ({
  challengeId,
  moveState,
  operation,
  userId,
}) => {
  if (!challengeId || !operation || !userId) {
    throw new Error('Missing challengeId, name, operation or userId');
  }

  const [isSmsVerifying, setIsSmsVerifying] = useState(false);
  const [wrongPasscode, setWrongPasscode] = useState(false);
  const [generateShortLivedToken] = useMutation(
    GenerateShortLivedTokenDocument,
  );

  const device = useDevice();

  const handlePasscodeComplete = async (
    passcode: string,
    clear: ClearAllDigitsFn,
  ) => {
    setIsSmsVerifying(true);

    try {
      const token = await generateShortLivedToken({
        variables: {
          input: { deviceToken: device.token, passcode, scopes: [] },
        },
      });
      if (token) {
        moveState(ChallengeRequestScreenState.Approved);
      } else {
        clear({ andFocusFirstInput: true });
        setWrongPasscode(true);
        setIsSmsVerifying(false);
      }
    } catch (error) {
      clear({ andFocusFirstInput: true });
      setWrongPasscode(true);
      setIsSmsVerifying(false);
    }
  };

  return (
    <>
      <SunshineModal.Body minHeight={modalMinHeight}>
        <YStack gap="$space.32">
          <YStack alignItems="flex-start" gap="$space.8">
            <Typography.Header textAlign="left">
              <FormattedMessage id="authentication.challenge.passcode.title" />
            </Typography.Header>
            <Typography.Text textAlign="justify" variant="secondary">
              <FormattedMessage id="authentication.challenge.passcode.description" />
            </Typography.Text>
            <Typography.Text bold textAlign="justify" variant="discreet">
              <FormattedMessage id="authentication.challenge.passcode.note" />
            </Typography.Text>
          </YStack>

          <YStack alignItems="center" gap="$space.16">
            <PasscodeInput hidden={true} onSubmit={handlePasscodeComplete} />

            {wrongPasscode ? (
              <Typography.Text textAlign="justify" variant="error">
                <FormattedMessage id="authentication.challenge.passcode.error" />
              </Typography.Text>
            ) : null}
          </YStack>
        </YStack>
      </SunshineModal.Body>
      <SunshineModal.Footer flexDirection="row">
        <View marginLeft="75%">
          <Button isDisabled={!isSmsVerifying} isLoading={isSmsVerifying}>
            <FormattedMessage id="authentication.challenge.passcode.submit" />
          </Button>
        </View>
      </SunshineModal.Footer>
    </>
  );
};

export default ChallengeRequestPasscode;
